import { state, api, $ as qs, $$, toast, escHtml } from './core';
import { askText, askChoice, askConfirm } from './dialogs';

declare function loadOverview(): Promise<void>;

/** Narrow DOM helper: template uses many `#id` nodes as form controls or panels. */
function $(sel: string, ctx: ParentNode = document): any {
    return qs(sel, ctx);
}

/* ═══ LIFECYCLE BADGE HELPERS ════════════════════════════════════════
 * Folosit pentru badge-ul „vârstă utilaj" din modalul de editare și din
 * Overview Fabrică. Se bazează pe `commissioning_date` (sau fallback
 * `manufacture_date`) și pe statutul preluat din endpoint-ul lifecycle.
 * Adăugat 14 mai 2026 (vezi routes/config.py — _compute_lifecycle_payload).
 */
export interface LifecycleInfo {
    machine_id?: string;
    commissioning_date: string | null;
    manufacture_date: string | null;
    commissioning_notes: string;
    age_days: number | null;
    age_years: number | null;
    warranty_status: 'in_warranty' | 'out_of_warranty' | 'unknown';
    battery_risk: 'low' | 'medium' | 'high' | 'unknown';
    battery_eligible: boolean;
}

export function buildLifecycleBadge(info: LifecycleInfo): { html: string; cls: string; text: string } {
    if (info.age_years == null) {
        return {
            html: `<span class="lifecycle-badge lc-unknown" title="Pune o dată de punere în funcțiune pentru a vedea vârsta">Necunoscut</span>`,
            cls: 'lc-unknown',
            text: 'Necunoscut',
        };
    }
    const yrs = info.age_years;
    const days = info.age_days || 0;
    if (info.battery_eligible && yrs >= 5) {
        const t = `${yrs.toFixed(1)} ani — verifică baterii`;
        return { html: `<span class="lifecycle-badge lc-battery-high" title="Recomandare service Sumitomo: schimbare baterii la 5 ani">${t}</span>`, cls: 'lc-battery-high', text: t };
    }
    if (info.battery_eligible && yrs >= 4) {
        const t = `${yrs.toFixed(1)} ani — atenție baterii`;
        return { html: `<span class="lifecycle-badge lc-battery" title="Apropie-te de pragul de 5 ani — baterii control">${t}</span>`, cls: 'lc-battery', text: t };
    }
    if (yrs < 1) {
        const months = Math.max(0, Math.round(days / 30));
        const t = months > 0 ? `Nou (${months} lun${months === 1 ? 'ă' : 'i'})` : 'Nou';
        return { html: `<span class="lifecycle-badge lc-new" title="Pus în funcțiune de mai puțin de 1 an">${t}</span>`, cls: 'lc-new', text: t };
    }
    if (yrs < 2) {
        const t = `În garanție (${yrs.toFixed(1)} ani)`;
        return { html: `<span class="lifecycle-badge lc-warranty" title="Sub 2 ani — încă în garanție conform regulii generale">${t}</span>`, cls: 'lc-warranty', text: t };
    }
    const t = `${yrs.toFixed(1)} ani`;
    return { html: `<span class="lifecycle-badge lc-mature" title="Pus în funcțiune acum ${yrs.toFixed(1)} ani">${t}</span>`, cls: 'lc-mature', text: t };
}

const _RO_MONTHS_SHORT = ['ian', 'feb', 'mar', 'apr', 'mai', 'iun', 'iul', 'aug', 'sep', 'oct', 'nov', 'dec'];
export function formatRoDate(iso: string | null | undefined): string {
    if (!iso) return '';
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
    if (!m) return iso;
    const day = parseInt(m[3], 10);
    const month = _RO_MONTHS_SHORT[parseInt(m[2], 10) - 1] || m[2];
    return `${day} ${month} ${m[1]}`;
}

export function formatRelativeAge(days: number | null | undefined): string {
    if (days == null || days < 0) return '';
    if (days < 30) return `acum ${days} zile`;
    const months = Math.round(days / 30);
    if (months < 12) return `acum ${months} lun${months === 1 ? 'ă' : 'i'}`;
    const years = days / 365.25;
    if (years < 2) return `acum ${years.toFixed(1)} an${years < 1.5 ? '' : 'i'}`;
    return `acum ${years.toFixed(1)} ani`;
}


/* ═══ CONFIG MODALS ════════════════════════════════════════════ */
export let _machineTypes: any[] = [];

export async function loadMachineTypes(): Promise<any[]> {
    if (_machineTypes.length) return _machineTypes;
    _machineTypes = await api('/api/config/machine_types');
    return _machineTypes;
}

export function populateTypeSelect(selectEl: HTMLElement): void {
    selectEl.innerHTML = '';
    _machineTypes.forEach(t => {
        selectEl.innerHTML += `<option value="${t.id}">${t.label}</option>`;
    });
}

$$('[data-close]').forEach((btn: Element) => {
    btn.addEventListener('click', () => {
        const id = (btn as HTMLElement).dataset.close;
        document.getElementById(id as string)?.classList.add('hidden');
    });
});

export async function openAddLineModal(): Promise<void> {
    await loadMachineTypes();
    populateTypeSelect($('#cfg-line-machine-type'));
    $('#cfg-line-id').value = '';
    $('#cfg-line-product').value = '';
    $('#cfg-line-type').value = '';
    $('#cfg-line-icon').value = '🏭';
    $('#cfg-line-production-rate').value = '0';
    $('#cfg-line-group-type').value = 'production';
    $('#cfg-line-machine-name').value = '';
    $('#cfg-line-manufacturer').value = '';
    $('#cfg-line-model').value = '';
    $('#cfg-line-sn').value = '';
    $('#cfg-line-notes').value = '';
    $('#modal-add-line').classList.remove('hidden');
}

$('#btn-save-line').addEventListener('click', async () => {
    const lid = $('#cfg-line-id').value.trim().toUpperCase();
    const product = $('#cfg-line-product').value.trim();
    if (!lid || !product) { toast('ID și Produs obligatorii!'); return; }

    const payload = {
        id: lid,
        product: product,
        line_type: $('#cfg-line-type').value.trim(),
        group_type: $('#cfg-line-group-type').value,
        icon: $('#cfg-line-icon').value.trim() || '🏭',
        production_rate: parseFloat($('#cfg-line-production-rate').value) || 0,
        main_machine_name: $('#cfg-line-machine-name').value.trim(),
        main_machine_type: $('#cfg-line-machine-type').value,
        manufacturer: $('#cfg-line-manufacturer').value.trim(),
        model: $('#cfg-line-model').value.trim(),
        serial_number: $('#cfg-line-sn').value.trim(),
        notes: $('#cfg-line-notes').value.trim(),
    };

    try {
        const res = await fetch('/api/config/lines', { method: 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify(payload) });
        const data = await res.json();
        if (!res.ok) { toast(data.error || 'Eroare'); return; }
        toast(`Linia ${lid} creată cu succes!`);
        $('#modal-add-line').classList.add('hidden');
        loadOverview();
    } catch (e) { toast('Eroare: ' + (e as Error).message); }
});

export async function openAddEquipModal(prefillLine?: string): Promise<void> {
    await loadMachineTypes();
    populateTypeSelect($('#cfg-eq-type'));
    $('#cfg-eq-id').value = '';
    $('#cfg-eq-name').value = '';
    $('#cfg-eq-type').value = 'auxiliary';
    $('#cfg-eq-manufacturer').value = '';
    $('#cfg-eq-model').value = '';
    $('#cfg-eq-sn').value = '';
    $('#cfg-eq-product').value = '';
    $('#cfg-eq-notes').value = '';
    const connType = document.getElementById('cfg-eq-connection-type') as HTMLSelectElement | null;
    const connIp = document.getElementById('cfg-eq-connection-ip') as HTMLInputElement | null;
    const connPort = document.getElementById('cfg-eq-connection-port') as HTMLInputElement | null;
    if (connType) connType.value = '';
    if (connIp) connIp.value = '';
    if (connPort) connPort.value = '';
    $('#cfg-eq-telemetry-id').textContent = '—';
    await buildLinesCheckboxes('cfg-eq-lines-checkboxes', prefillLine || '');

    const linkedWrap = document.getElementById('cfg-eq-linked-wrap') as HTMLElement;
    linkedWrap.style.display = 'none';
    const cavitiesWrap = document.getElementById('cfg-eq-cavities-wrap');
    if (cavitiesWrap) cavitiesWrap.style.display = 'none';
    const cavInput = document.getElementById('cfg-eq-cavities');
    if (cavInput) (cavInput as HTMLInputElement).value = '';
    const linkedSel = document.getElementById('cfg-eq-linked-machine') as HTMLSelectElement;
    linkedSel.innerHTML = '<option value="">— neselectat —</option>';

    const sensorsEl = $('#cfg-eq-sensors');
    sensorsEl.innerHTML = '<div style="color:var(--text-muted);font-size:.65rem;margin-bottom:.3rem">Key · Label · Unit · Bază · Var% · ⚠Warn · 🔴Crit · Sursă (De la utilaj / Extern) · IP/Port</div>';

    const scanStatus = document.getElementById('scan-label-status-add');
    const scanPreview = document.getElementById('scan-label-preview-add');
    if (scanStatus) scanStatus.innerHTML = '';
    if (scanPreview) scanPreview.innerHTML = '';

    $('#modal-add-equip').classList.remove('hidden');
}

/* ═══ SENZORI COMUNI (o sursă, mai multe utilaje, praguri diferite) ═══════════════════ */
export async function openSharedSensorsModal(): Promise<void> {
    if (state.currentUser && state.currentUser.role !== 'master') {
        toast('Doar nivel master poate configura senzorii comuni.', 'error');
        return;
    }
    const modal = document.getElementById('modal-shared-sensors');
    if (modal) modal.classList.remove('hidden');
    await renderSharedSensorsList();
}

export async function renderSharedSensorsList(): Promise<void> {
    const listEl = document.getElementById('shared-sensors-list');
    if (!listEl) return;
    try {
        const sensors = await api('/api/config/shared_sensors');
        if (!sensors || !sensors.length) {
            listEl.innerHTML = '<p class="shared-sensors-empty">Niciun senzor comun. Apasă „Adaugă senzor comun" pentru a defini o sursă (ex. aer rețea) și a o aloca la mai multe utilaje cu praguri diferite.</p>';
            return;
        }
        let html = '';
        for (const ss of sensors) {
            const machines = await api(`/api/config/shared_sensors/${ss.id}/machines`);
            html += `<div class="shared-sensor-card" data-id="${ss.id}">
                <div class="shared-sensor-header">
                    <strong>${escHtml(ss.name || ss.sensor_key)}</strong>
                    <span class="shared-sensor-meta">key: <code>${escHtml(ss.sensor_key)}</code> ${ss.connection_ip ? ' · ' + escHtml(ss.connection_ip) : ''} ${ss.unit ? ' · ' + escHtml(ss.unit) : ''}</span>
                    <span class="shared-sensor-actions">
                        <button type="button" class="btn btn-sm shared-sensor-assign" data-id="${ss.id}">Asociază utilaj</button>
                        <button type="button" class="btn btn-sm btn-red shared-sensor-delete" data-id="${ss.id}">Șterge</button>
                    </span>
                </div>
                <div class="shared-sensor-machines">
                    ${machines.length ? `<table class="shared-sensor-table"><thead><tr><th>Utilaj</th><th>⚠ Warn</th><th>🔴 Crit</th><th></th></tr></thead><tbody>
                        ${machines.map((m: any) => `<tr>
                            <td>${escHtml(m.machine_name || m.machine_id)}</td>
                            <td>${Number(m.warn_threshold)}</td>
                            <td>${Number(m.crit_threshold)}</td>
                            <td><button type="button" class="btn-link shared-unassign" data-sid="${ss.id}" data-mid="${String(m.machine_id).replace(/"/g, '&quot;')}">Elimină</button></td>
                        </tr>`).join('')}
                    </tbody></table>` : '<p class="no-assignments">Niciun utilaj alocat. Apasă „Asociază utilaj" și setează pragurile per utilaj.</p>'}
                </div>
            </div>`;
        }
        listEl.innerHTML = html;
        listEl.querySelectorAll('.shared-sensor-assign').forEach((btn: Element) => {
            (btn as HTMLElement).onclick = () => openAssignSharedSensorDialog(parseInt((btn as HTMLElement).dataset.id as string, 10));
        });
        listEl.querySelectorAll('.shared-sensor-delete').forEach((btn: Element) => {
            (btn as HTMLElement).onclick = async () => {
                if (!await askConfirm('Ștergi acest senzor comun? Se vor elimina și alocările.')) return;
                try {
                    const r = await fetch(`/api/config/shared_sensors/${(btn as HTMLElement).dataset.id}`, { method: 'DELETE', credentials: 'include' });
                    if (!r.ok) { toast((await r.json()).error || 'Eroare'); return; }
                    toast('Senzor comun șters.');
                    await renderSharedSensorsList();
                } catch (e) { toast('Eroare: ' + (e as Error).message); }
            };
        });
        listEl.querySelectorAll('.shared-unassign').forEach((btn: Element) => {
            (btn as HTMLElement).onclick = async () => {
                try {
                    const r = await fetch(`/api/config/shared_sensors/${(btn as HTMLElement).dataset.sid}/machines/${encodeURIComponent((btn as HTMLElement).dataset.mid as string)}`, { method: 'DELETE', credentials: 'include' });
                    if (!r.ok) { toast((await r.json()).error || 'Eroare'); return; }
                    toast('Alocare eliminată.');
                    await renderSharedSensorsList();
                } catch (e) { toast('Eroare: ' + (e as Error).message); }
            };
        });
    } catch (e) {
        listEl.innerHTML = '<p class="shared-sensors-empty">Eroare la încărcare: ' + escHtml((e as Error).message) + '</p>';
    }
}

export async function openAssignSharedSensorDialog(sharedId: number): Promise<void> {
    const machines = await api('/api/machines/all');
    const assigned = await api(`/api/config/shared_sensors/${sharedId}/machines`);
    const assignedIds = new Set((assigned || []).map((a: any) => a.machine_id));
    const available = (machines || []).filter((m: any) => !assignedIds.has(m.id));
    if (!available.length) { toast('Toate utilajele sunt deja alocate la acest senzor.'); return; }
    const machineId = await askText('Alocă utilaj la senzor', {
        label: 'Utilaje disponibile:\n' + available.map((m: any) => m.id + ' — ' + (m.name || '')).join('\n'),
        placeholder: 'ID utilaj (ex. COMPRESOR_GA26)', required: true, okText: 'Alocă',
    });
    if (!machineId) return;
    const mid = machineId.trim().split(/\s/)[0];
    if (!available.some((m: any) => m.id === mid)) { toast('Utilaj invalid sau deja alocat.'); return; }
    const warnStr = await askText('Prag avertizare (Warn)', { value: '0', inputMode: 'decimal' });
    if (warnStr === null) return;
    const critStr = await askText('Prag critic (Crit)', { value: '0', inputMode: 'decimal' });
    if (critStr === null) return;
    const warn = parseFloat(warnStr || '0') || 0;
    const crit = parseFloat(critStr || '0') || 0;
    try {
        const r = await fetch(`/api/config/shared_sensors/${sharedId}/machines`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ machine_id: mid, warn_threshold: warn, crit_threshold: crit })
        });
        const data = await r.json();
        if (!r.ok) { toast(data.error || 'Eroare'); return; }
        toast('Utilaj alocat cu pragurile setate.');
        await renderSharedSensorsList();
    } catch (e) { toast('Eroare: ' + (e as Error).message); }
}

document.getElementById('btn-add-shared-sensor')?.addEventListener('click', async () => {
    if (state.currentUser && state.currentUser.role !== 'master') { toast('Doar master.'); return; }
    const name = await askText('Senzor comun nou', { label: 'Nume senzor (ex. Aer rețea):', required: true });
    if (name == null || !name.trim()) return;
    const sensor_key = await askText('Cheie pentru telemetrie', {
        label: 'Ex. pressure_air', value: name.toLowerCase().replace(/\s+/g, '_'), required: true,
    });
    if (sensor_key == null || !sensor_key.trim()) return;
    const connection_ip = await askText('Adresă IP (opțional)', {});
    if (connection_ip === null) return;
    const unit = await askText('Unitate de măsură', { value: 'bar', label: 'Ex. bar, °C' });
    if (unit === null) return;
    try {
        const r = await fetch('/api/config/shared_sensors', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ name: name.trim(), sensor_key: sensor_key.trim(), connection_ip: (connection_ip || '').trim(), unit: (unit || '').trim(), label: name.trim() })
        });
        const data = await r.json();
        if (!r.ok) { toast(data.error || 'Eroare'); return; }
        toast('Senzor comun adăugat.');
        await renderSharedSensorsList();
    } catch (e) { toast('Eroare: ' + (e as Error).message); }
});

/* ═══ CONTOARE (PAC3120/PAC3220: protocoale, alocare la utilaje) ═══════════════════ */
export const PROTOCOL_LABELS = {
    modbus_tcp: 'Modbus TCP (Ethernet)', modbus_rtu: 'Modbus RTU (serial)', opc_ua: 'OPC UA', ethernet: 'Ethernet (generic)',
    // Cadre TCP brute, nesolicitate, de la PLC (portul 4002): foldare/SFM/verificare = 6 bytes
    // producție; linia robot = 24 bytes SendData cu biți de alarmă (9 sep 2026).
    tcp_foldare: 'TCP brut PLC (cadre 6B — producție)', tcp_robot: 'TCP brut PLC (SendData 24B — alarme linie robot)',
    // imprimantele Zebra din rețea: doar pagina web, portul 80 (26 oct 2026) — portul 9100 rămâne al MES
    zebra_http: 'Zebra — pagina web (doar citire)',
} as const;
export const CONNECTION_PROTOCOL_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
    { value: '', label: '— Selectează protocol' },
    { value: 'modbus_tcp', label: 'Modbus TCP' },
    { value: 'opc_ua', label: 'OPC UA' },
    { value: 'modbus_rtu', label: 'Modbus RTU (serial)' },
];

export async function openCountersModal(): Promise<void> {
    if (state.currentUser && state.currentUser.role !== 'master') {
        toast('Doar nivel master poate configura contoarele.', 'error');
        return;
    }
    const modal = document.getElementById('modal-counters');
    if (modal) modal.classList.remove('hidden');
    await Promise.all([renderLiveSourcesList(), renderCountersList()]);
}

export async function renderLiveSourcesList(): Promise<void> {
    const el = document.getElementById('counters-sources-live');
    if (!el) return;
    try {
        const [sourcesData, sensorData] = await Promise.all([
            api('/api/collector/sources').catch(() => ({ sources: [] })),
            api('/api/telemetry/sensors/latest').catch(() => ({}))
        ]);
        const sources = sourcesData.sources || sourcesData || [];
        if (!sources.length) {
            el.innerHTML = '<p style="font-size:.8rem;color:var(--text-muted);">Nicio sursă configurată. Configurați conexiunile la fiecare utilaj (tab Conectare).</p>';
            return;
        }
        const typeLabels = { meter: 'Contor curent', machine_plc: 'PLC / Utilaj', sensor_external: 'Senzor extern', shared_sensor: 'Senzor comun' };
        const typeColors = { meter: '#00e676', machine_plc: '#00b4ff', sensor_external: '#ffaa00', shared_sensor: '#aa66ff' };

        const byType: Record<string, any[]> = {};
        sources.forEach((s: any) => {
            const t = s.type || 'unknown';
            if (!byType[t]) byType[t] = [];
            byType[t].push(s);
        });

        let html = `<div style="font-size:.75rem;color:var(--text-muted);margin-bottom:.5rem;">Total: <strong>${sources.length}</strong> surse active</div>`;

        for (const [type, srcs] of Object.entries(byType)) {
            const tl = (typeLabels as any)[type] || type;
            const tc = (typeColors as any)[type] || '#888';
            html += `<div class="source-group" style="margin-bottom:.8rem;">
                <div style="font-size:.7rem;font-weight:700;color:${tc};text-transform:uppercase;margin-bottom:.3rem;letter-spacing:.5px;">${escHtml(tl)} (${srcs.length})</div>`;
            srcs.forEach((src: any) => {
                const proto = (src.protocol || '').toUpperCase().replace('_', ' ');
                const mid = src.machine_id || src.sensor_key || '—';
                const addr = `${src.ip || ''}:${src.port || ''}`;
                const devType = src.device_type ? ` · ${src.device_type}` : '';
                const sd = sensorData[mid];
                let lastUpdate = '';
                if (sd) {
                    let latest = 0;
                    Object.values(sd).forEach((v: any) => {
                        if (v && v.updated_at) {
                            const t = new Date(v.updated_at).getTime();
                            if (t > latest) latest = t;
                        }
                    });
                    if (latest) {
                        const ageSec = Math.round((Date.now() - latest) / 1000);
                        const ageStr = ageSec < 60 ? `${ageSec}s` : ageSec < 3600 ? `${Math.floor(ageSec/60)}m` : `${Math.floor(ageSec/3600)}h ${Math.floor((ageSec%3600)/60)}m`;
                        const fresh = ageSec < 30;
                        lastUpdate = `<span style="color:${fresh ? 'var(--green)' : 'var(--yellow)'};font-size:.65rem;"> ${fresh ? '● LIVE' : '○ ' + ageStr + ' în urmă'}</span>`;
                    }
                }
                html += `<div style="display:flex;align-items:center;gap:.5rem;padding:.25rem .4rem;border-bottom:1px solid rgba(255,255,255,.04);font-size:.75rem;">
                    <strong style="min-width:6rem;">${escHtml(mid)}</strong>
                    <span style="color:var(--text-muted);">${escHtml(proto)} ${escHtml(addr)}${escHtml(devType)}</span>
                    ${lastUpdate}
                </div>`;
            });
            html += `</div>`;
        }
        el.innerHTML = html;
    } catch (e) {
        el.innerHTML = '<p style="color:var(--red);font-size:.8rem;">Eroare la încărcare surse: ' + escHtml((e as Error).message) + '</p>';
    }
}

export async function openCollectorModal(): Promise<void> {
    if (state.currentUser && state.currentUser.role !== 'master') {
        toast('Doar nivel master poate gestiona colectorul.', 'error');
        return;
    }
    const modal = document.getElementById('modal-collector');
    if (modal) modal.classList.remove('hidden');
    const statusEl = document.getElementById('collector-status-text');
    const detailsEl = document.getElementById('collector-details');
    const toggleEl = document.getElementById('collector-enabled') as HTMLInputElement | null;
    const labelEl = document.getElementById('collector-toggle-label');
    const remoteEl = document.getElementById('collector-remote-section');
    if (!toggleEl) return;
    const refresh = async () => {
        try {
            const [s, sourcesData] = await Promise.all([
                api('/api/collector/status'),
                api('/api/collector/sources').catch(() => ({ sources: [] }))
            ]);
            const sources = sourcesData.sources || sourcesData || [];
            const rc = s.remote_collector || {};
            if (remoteEl) {
                const isOnline = rc.online;
                const hb = rc.last_heartbeat ? new Date(rc.last_heartbeat) : null;
                const hbAge = hb ? Math.round((Date.now() - hb.getTime()) / 1000) : null;
                const hbStr = hbAge != null ? (hbAge < 60 ? `${hbAge}s` : hbAge < 3600 ? `${Math.floor(hbAge / 60)}m` : `${Math.floor(hbAge / 3600)}h`) : '—';
                const protos = rc.protocols || {};
                const protoList = Object.entries(protos).filter(([_, v]) => v).map(([k]) => k.toUpperCase().replace('_', ' ')).join(', ');

                const meterSources = sources.filter((s: any) => s.type === 'meter');
                const plcSources = sources.filter((s: any) => s.type === 'machine_plc');
                const sensorSources = sources.filter((s: any) => s.type === 'sensor_external' || s.type === 'shared_sensor');

                let html = `<div class="collector-section-title" style="font-size:.75rem;color:var(--text-muted);margin-bottom:.4rem;">Colector remote</div>`;
                html += `<div class="collector-remote-status" style="display:flex;align-items:center;gap:.5rem;margin-bottom:.5rem;">
                    <span class="collector-dot ${isOnline ? 'online' : 'offline'}"></span>
                    <strong style="color:${isOnline ? 'var(--green)' : 'var(--red)'};">${isOnline ? 'ONLINE' : 'OFFLINE'}</strong>
                    <span style="font-size:.75rem;color:var(--text-muted);">Heartbeat: ${hbStr} în urmă</span>
                </div>`;
                html += `<div style="display:grid;grid-template-columns:repeat(3,1fr);gap:.4rem;margin:.5rem 0;">
                    <div class="collector-stat"><span class="stat-num" style="color:var(--green)">${rc.ok || 0}</span><span class="stat-label">OK</span></div>
                    <div class="collector-stat"><span class="stat-num" style="color:var(--red)">${rc.fail || 0}</span><span class="stat-label">Eșec</span></div>
                    <div class="collector-stat"><span class="stat-num">${rc.sources || sources.length}</span><span class="stat-label">Total surse</span></div>
                </div>`;
                if (protoList) html += `<div style="font-size:.7rem;color:var(--text-muted);margin:.3rem 0;">Protocoale: ${escHtml(protoList)}</div>`;

                // R5 (28 sep 2026): fiecare colector cu rândul lui — înainte se vedea
                // doar ultimul care bătuse la procesul care răspundea.
                const colectoare: any[] = Array.isArray(s.colectoare) ? s.colectoare : [];
                if (colectoare.length) {
                    const vii = colectoare.filter((c: any) => c.online).length;
                    const cat = (sec: number | null): string => sec == null ? '—'
                        : sec < 60 ? `${sec} s` : sec < 3600 ? `${Math.floor(sec / 60)} min`
                        : sec < 86400 ? `${Math.floor(sec / 3600)} h` : `${Math.floor(sec / 86400)} zile`;
                    html += `<div class="collector-section-title" style="font-size:.75rem;color:var(--text-muted);margin:.6rem 0 .3rem;">Colectoare: ${vii} online din ${colectoare.length}</div>`;
                    html += `<div class="colectoare-tabel-wrap"><table class="colectoare-tabel"><thead><tr>
                        <th>Colector</th><th>Versiune</th><th>Surse</th><th>Neprimite încă</th><th>Ultimul semnal</th></tr></thead><tbody>`;
                    colectoare.forEach((c: any) => {
                        const nume = String(c.colector || '').replace(/^colector_/, '');
                        const restanta = c.buffer_intrari
                            ? `${c.buffer_intrari} citiri${c.buffer_cel_mai_vechi ? ', din ' + String(c.buffer_cel_mai_vechi).replace('T', ' ').slice(0, 16) : ''}`
                            : '—';
                        const note: string[] = [];
                        if (c.surse_din_cache) note.push('citește din lista salvată (serverul nu i-a răspuns)');
                        if (c.server_jos_de_la) note.push('serverul căzut de la ' + String(c.server_jos_de_la).replace('T', ' ').slice(0, 16));
                        const erori = Array.isArray(c.last_errors) ? c.last_errors.slice(-3).join(' · ') : '';
                        // în atribut: escHtml nu scapă ghilimelele, iar erorile vin de pe PC
                        const erAttr = erori ? escHtml(erori).replace(/"/g, '&quot;') : '';
                        html += `<tr class="${c.online ? '' : 'colector-tacut'}" title="${erAttr}">
                            <td class="col-nume"><span class="collector-dot ${c.online ? 'online' : 'offline'}"></span> ${escHtml(nume)}${note.length ? `<div class="colector-nota">${escHtml(note.join(' · '))}</div>` : ''}</td>
                            <td class="col-versiune">${escHtml(c.build || '—')}</td>
                            <td>${c.sources === 0
                                ? '<span style="color:var(--text-muted)">fără surse</span>'
                                : `${c.ok ?? '—'} ok${c.fail ? ` / <span style="color:var(--red)">${c.fail} eșec</span>` : ''}`}</td>
                            <td>${escHtml(restanta)}</td>
                            <td>${escHtml(cat(c.secunde_de_la_ultimul_semnal))}</td></tr>`;
                    });
                    html += `</tbody></table></div>`;
                }

                html += `<details style="margin-top:.5rem;"><summary style="cursor:pointer;font-size:.75rem;color:var(--accent);">Detalii surse (${sources.length})</summary>`;
                html += `<div class="collector-sources-list" style="margin-top:.4rem;max-height:200px;overflow-y:auto;">`;
                const typeLabels = { meter: 'Contor', machine_plc: 'PLC/Utilaj', sensor_external: 'Senzor extern', shared_sensor: 'Senzor comun' };
                sources.forEach((src: any) => {
                    const tl = (typeLabels as any)[src.type] || src.type;
                    const proto = (src.protocol || '').toUpperCase().replace('_', ' ');
                    html += `<div class="collector-source-row" style="display:flex;gap:.5rem;align-items:center;padding:.2rem 0;font-size:.7rem;border-bottom:1px solid rgba(255,255,255,.04);">
                        <span class="badge badge-blue" style="font-size:.6rem;padding:.1rem .3rem;">${escHtml(tl)}</span>
                        <strong>${escHtml(src.machine_id || src.sensor_key || '—')}</strong>
                        <span style="color:var(--text-muted);">${escHtml(proto)} ${escHtml(src.ip || '')}:${src.port || ''}</span>
                    </div>`;
                });
                html += `</div></details>`;
                remoteEl.innerHTML = html;
            }

            if (statusEl) statusEl.textContent = s.enabled ? 'Activ' : 'Inactiv';
            if (labelEl) labelEl.textContent = s.enabled ? 'Oprește colectorul' : 'Pornește colectorul';
            toggleEl.checked = !!s.enabled;
            let details = '';
            if (s.has_pymodbus === false) details = 'Lipsește pymodbus. Pe server rulați: pip install pymodbus';
            else if (s.last_cycle_at) details = `Ultimul ciclu: ${s.last_cycle_at} · Surse: ${s.sources_count || 0} · OK: ${s.meters_ok || 0}${(s.meters_fail || 0) ? ' · Eșec: ' + s.meters_fail : ''}`;
            else if (s.sources_count != null && s.sources_count === 0) details = 'Colectorul local nu are surse proprii. Folosește colectorul remote.';
            if (s.error) details = (details ? details + ' · ' : '') + 'Eroare: ' + s.error;
            if (detailsEl) detailsEl.textContent = details;
        } catch (e) {
            if (statusEl) statusEl.textContent = 'Eroare la încărcare status';
            if (detailsEl) detailsEl.textContent = (e as Error).message || 'Eroare';
        }
    };
    toggleEl.onchange = async () => {
        try {
            await fetch('/api/collector/enabled', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ enabled: toggleEl.checked })
            });
            toast(toggleEl.checked ? 'Colector pornit.' : 'Colector oprit.');
            await refresh();
        } catch (e) {
            toast('Eroare: ' + ((e as Error).message || 'nu s-a putut actualiza'));
            await refresh();
        }
    };
    await refresh();
}

export async function renderCountersList(): Promise<void> {
    const listEl = document.getElementById('counters-list');
    if (!listEl) return;
    try {
        const counters = await api('/api/counters');
        if (!counters || !counters.length) {
            listEl.innerHTML = '<p class="counters-empty">Niciun contor. Apasă „Adaugă contor" și completează protocolul (ex. Modbus TCP pentru PAC3220, Modbus RTU pentru PAC3120), IP și port.</p>';
            return;
        }
        let html = '';
        for (const c of counters) {
            const protoLabel = (PROTOCOL_LABELS as any)[c.protocol] || c.protocol;
            const connStr = [c.host, c.port != null ? ':' + c.port : '', c.slave_id != null ? ' (slave ' + c.slave_id + ')' : ''].filter(Boolean).join('');
            const machineIds = c.machine_ids || [];
            html += `<div class="counter-card" data-id="${c.id}">
                <div class="counter-header">
                    <strong>${escHtml(c.name)}</strong>
                    <span class="counter-meta">${escHtml(protoLabel)} ${connStr ? ' · ' + escHtml(connStr) : ''}</span>
                    <span class="counter-actions">
                        <button type="button" class="btn btn-sm counter-assign" data-id="${c.id}">Alocă utilaj</button>
                        <button type="button" class="btn btn-sm counter-edit" data-id="${c.id}">Editează</button>
                        <button type="button" class="btn btn-sm btn-red counter-delete" data-id="${c.id}">Șterge</button>
                    </span>
                </div>
                <div class="counter-machines">
                    ${machineIds.length ? '<span class="counter-tags">Alocat la: ' + machineIds.map((mid: any) => `<code>${escHtml(mid)}</code> <button type="button" class="btn-link counter-unassign" data-cid="${c.id}" data-mid="${String(mid).replace(/"/g, '&quot;')}">Elimină</button>`).join(', ') + '</span>' : '<span class="no-assignments">Niciun utilaj alocat. Apasă „Alocă utilaj".</span>'}
                </div>
            </div>`;
        }
        listEl.innerHTML = html;
        listEl.querySelectorAll('.counter-assign').forEach((btn: Element) => {
            (btn as HTMLElement).onclick = () => openAssignCounterDialog(parseInt((btn as HTMLElement).dataset.id as string, 10));
        });
        listEl.querySelectorAll('.counter-edit').forEach((btn: Element) => {
            (btn as HTMLElement).onclick = () => openEditCounterDialog(parseInt((btn as HTMLElement).dataset.id as string, 10));
        });
        listEl.querySelectorAll('.counter-delete').forEach((btn: Element) => {
            (btn as HTMLElement).onclick = async () => {
                if (!await askConfirm('Ștergi acest contor? Alocările la utilaje se vor elimina.')) return;
                try {
                    const r = await fetch(`/api/counters/${(btn as HTMLElement).dataset.id}`, { method: 'DELETE', credentials: 'include' });
                    if (!r.ok) { toast((await r.json()).error || 'Eroare'); return; }
                    toast('Contor șters.');
                    await renderCountersList();
                } catch (e) { toast('Eroare: ' + (e as Error).message); }
            };
        });
        listEl.querySelectorAll('.counter-unassign').forEach((btn: Element) => {
            (btn as HTMLElement).onclick = async () => {
                try {
                    const r = await fetch(`/api/counters/${(btn as HTMLElement).dataset.cid}/machines/${encodeURIComponent((btn as HTMLElement).dataset.mid as string)}`, { method: 'DELETE', credentials: 'include' });
                    if (!r.ok) { toast((await r.json()).error || 'Eroare'); return; }
                    toast('Alocare eliminată.');
                    await renderCountersList();
                } catch (e) { toast('Eroare: ' + (e as Error).message); }
            };
        });
    } catch (e) {
        listEl.innerHTML = '<p class="counters-empty">Eroare la încărcare: ' + escHtml((e as Error).message) + '</p>';
    }
}

export async function openAssignCounterDialog(counterId: number): Promise<void> {
    const [counters, machines] = await Promise.all([api('/api/counters'), api('/api/machines/all')]);
    const counter = (counters || []).find((c: any) => c.id === counterId);
    if (!counter) { toast('Contor negăsit.'); return; }
    const assigned = counter.machine_ids || [];
    const available = (machines || []).filter((m: any) => !assigned.includes(m.id));
    if (!available.length) { toast('Toate utilajele sunt deja alocate la acest contor.'); return; }
    const machineId = await askText('Alocă utilaj la contor', {
        label: 'Utilaje disponibile:\n' + available.map((m: any) => m.id + ' — ' + (m.name || '')).join('\n'),
        placeholder: 'ID utilaj', required: true, okText: 'Alocă',
    });
    if (!machineId) return;
    const mid = machineId.trim().split(/\s/)[0];
    if (!available.some((m: any) => m.id === mid)) { toast('Utilaj invalid sau deja alocat.'); return; }
    try {
        const r = await fetch(`/api/counters/${counterId}/machines`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ machine_id: mid })
        });
        const data = await r.json();
        if (!r.ok) { toast(data.error || 'Eroare'); return; }
        toast('Contor alocat la ' + mid);
        await renderCountersList();
    } catch (e) { toast('Eroare: ' + (e as Error).message); }
}

export async function openEditCounterDialog(counterId: number): Promise<void> {
    const c = await api(`/api/counters/${counterId}`);
    if (!c) { toast('Contor negăsit.'); return; }
    const name = await askText('Editare contor', { label: 'Nume contor:', value: c.name || '', required: true });
    if (name == null) return;
    const protocol = await askText('Protocol', { label: 'modbus_tcp, modbus_rtu, opc_ua sau ethernet', value: c.protocol || 'modbus_tcp', required: true });
    if (protocol == null) return;
    const host = await askText('Host / IP', { value: c.host || '' });
    if (host == null) return;
    const port = await askText('Port', { label: 'Ex. 502 pentru Modbus TCP', value: c.port != null ? String(c.port) : '502', inputMode: 'numeric' });
    if (port == null) return;
    const slaveId = await askText('Modbus slave ID', { label: '1–247', value: c.slave_id != null ? String(c.slave_id) : '1', inputMode: 'numeric' });
    if (slaveId == null) return;
    try {
        const r = await fetch(`/api/counters/${counterId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
                name: name.trim(),
                protocol: (protocol || 'modbus_tcp').trim().toLowerCase(),
                host: (host || '').trim() || null,
                port: port !== '' ? parseInt(port || '', 10) : null,
                slave_id: slaveId !== '' ? parseInt(slaveId || '', 10) : null
            })
        });
        const data = await r.json();
        if (!r.ok) { toast(data.error || 'Eroare'); return; }
        toast('Contor actualizat.');
        await renderCountersList();
    } catch (e) { toast('Eroare: ' + (e as Error).message); }
}

document.getElementById('btn-add-counter')?.addEventListener('click', async () => {
    if (state.currentUser && state.currentUser.role !== 'master') { toast('Doar master.'); return; }
    const name = await askText('Contor nou', { label: 'Nume (ex. Contor linie M1, PAC3220 hala):', required: true });
    if (name == null || !name.trim()) return;
    const protocol = await askText('Protocol', { label: 'modbus_tcp (PAC3220/Ethernet), modbus_rtu (PAC3120/serial), opc_ua, ethernet', value: 'modbus_tcp', required: true });
    if (protocol == null) return;
    const host = await askText('Host / Adresă IP', { placeholder: 'ex. 192.168.1.10' });
    if (host == null) return;
    const port = await askText('Port', { label: '502 pentru Modbus TCP', value: '502', inputMode: 'numeric' });
    if (port == null) return;
    const slaveId = await askText('Modbus slave ID', { label: '1–247, implicit 1', value: '1', inputMode: 'numeric' });
    if (slaveId == null) return;
    try {
        const r = await fetch('/api/counters', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
                name: name.trim(),
                protocol: (protocol || 'modbus_tcp').trim().toLowerCase(),
                host: (host || '').trim() || null,
                port: port !== '' ? parseInt(port || '', 10) : 502,
                slave_id: slaveId !== '' ? parseInt(slaveId || '', 10) : 1
            })
        });
        const data = await r.json();
        if (!r.ok) { toast(data.error || 'Eroare'); return; }
        toast('Contor adăugat. Alocă-l la utilaj din listă.');
        await renderCountersList();
    } catch (e) { toast('Eroare: ' + (e as Error).message); }
});

$('#cfg-eq-id').addEventListener('input', () => {
    const id = $('#cfg-eq-id').value.trim();
    $('#cfg-eq-telemetry-id').textContent = id || '—';
});

$('#cfg-eq-type').addEventListener('change', async () => {
    const wrap = document.getElementById('cfg-eq-linked-wrap') as HTMLElement;
    const cavitiesWrap = document.getElementById('cfg-eq-cavities-wrap');
    if ($('#cfg-eq-type').value === 'mold') {
        wrap.style.display = '';
        if (cavitiesWrap) cavitiesWrap.style.display = '';
        const allMach = await api('/api/machines/all');
        const sel = document.getElementById('cfg-eq-linked-machine') as HTMLSelectElement;
        sel.innerHTML = '<option value="">— neselectat —</option>';
        allMach.filter((m: any) => m.machine_type === 'injection' || m.machine_type === 'compression')
            .forEach((m: any) => {
                const opt = document.createElement('option');
                opt.value = m.id;
                opt.textContent = `${m.id} — ${m.name}`;
                sel.appendChild(opt);
            });
    } else {
        wrap.style.display = 'none';
        if (cavitiesWrap) cavitiesWrap.style.display = 'none';
    }
});

$('#btn-add-sensor-new').addEventListener('click', () => {
    $('#cfg-eq-sensors').appendChild(buildSensorConfigRow({
        sensor_key: '', label: '', unit: '°C', base_value: 25, variance_pct: 5, warn_threshold: 50, crit_threshold: 70
    }));
});

$('#btn-save-equip').addEventListener('click', async () => {
    const idEl = $('#cfg-eq-id');
    const nameEl = $('#cfg-eq-name');
    const mid = (idEl && idEl.value || '').trim();
    const name = (nameEl && nameEl.value || '').trim();
    const line = getCheckedLines('cfg-eq-lines-checkboxes');
    document.querySelectorAll('#modal-add-equip .invalid').forEach(el => el.classList.remove('invalid'));
    const missing = [];
    if (!mid) { if (idEl) { idEl.classList.add('invalid'); idEl.focus(); } missing.push('ID Utilaj'); }
    if (!name) { if (nameEl) { nameEl.classList.add('invalid'); if (!mid) nameEl.focus(); } missing.push('Nume Utilaj'); }
    if (!line) {
        const lineField = document.getElementById('cfg-eq-lines-checkboxes')?.closest('.cfg-field');
        if (lineField) lineField.classList.add('invalid');
        missing.push('cel puțin o linie bifată');
    }
    if (missing.length > 0) {
        toast('Completează: ' + missing.join(', ') + '.', 'error');
        return;
    }

    const payload: any = {
        id: mid, name: name,
        machine_type: $('#cfg-eq-type').value,
        manufacturer: $('#cfg-eq-manufacturer').value.trim(),
        model: $('#cfg-eq-model').value.trim(),
        serial_number: $('#cfg-eq-sn').value.trim(),
        product: $('#cfg-eq-product').value.trim(),
        line: line,
        notes: $('#cfg-eq-notes').value.trim(),
    };
    const ct = document.getElementById('cfg-eq-connection-type') as HTMLSelectElement | null;
    const ci = document.getElementById('cfg-eq-connection-ip') as HTMLInputElement | null;
    const cp = document.getElementById('cfg-eq-connection-port') as HTMLInputElement | null;
    if (ct) payload.connection_type = (ct.value || '').trim() || '';
    if (ci) payload.connection_ip = (ci.value || '').trim() || '';
    if (cp && cp.value !== '') payload.connection_port = cp.value;
    if ($('#cfg-eq-type').value === 'mold') {
        payload.linked_machine_id = (document.getElementById('cfg-eq-linked-machine') as HTMLSelectElement).value || '';
        const cav = document.getElementById('cfg-eq-cavities');
        payload.cavities = (cav && (cav as HTMLInputElement).value && parseInt((cav as HTMLInputElement).value, 10)) || null;
    }

    try {
        const res = await fetch('/api/config/machines', { method: 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify(payload) });
        const data = await res.json();
        if (!res.ok) { toast(data.error || 'Eroare'); return; }
        const sensors = getSensorConfigsFromContainer('cfg-eq-sensors');
        if (sensors.length > 0) {
            await fetch(`/api/config/sensors/${encodeURIComponent(mid)}`, { method: 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify({ sensors }) });
        }
        toast(`Utilajul ${mid} adăugat!`);
        $('#modal-add-equip').classList.add('hidden');
        loadOverview();
    } catch (e) { toast('Eroare: ' + (e as Error).message); }
});

export async function buildLinesCheckboxes(containerId: string, selectedLines: string): Promise<void> {
    const lines = await api('/api/config/lines');
    const container = document.getElementById(containerId) as HTMLElement;
    container.innerHTML = '';
    const selected = (selectedLines || '').split(',').map(s => s.trim()).filter(Boolean);
    lines.forEach((l: any) => {
        const isChecked = selected.includes(l.id);
        const div = document.createElement('label');
        div.className = `line-chk-item ${isChecked ? 'checked' : ''}`;
        div.innerHTML = `<input type="checkbox" value="${l.id}" ${isChecked ? 'checked' : ''}> ${l.id} <span style="color:var(--text-muted);font-size:.6rem">${escHtml(l.product).substring(0,15)}</span>`;
        (div.querySelector('input') as HTMLInputElement).addEventListener('change', (e) => {
            div.classList.toggle('checked', (e.target as HTMLInputElement).checked);
        });
        container.appendChild(div);
    });
}

export function getCheckedLines(containerId: string): string {
    const checks = document.querySelectorAll(`#${containerId} input[type="checkbox"]:checked`);
    return Array.from(checks).map(c => (c as HTMLInputElement).value).join(',');
}

/* ── Image compression before upload ── */
export function _compressImage(file: File, maxWidth?: number, maxHeight?: number, quality?: number): Promise<File> {
    maxWidth = maxWidth || 1920;
    maxHeight = maxHeight || 1920;
    quality = quality || 0.85;
    return new Promise(function(resolve: (f: File) => void) {
        if (file.size <= 4 * 1024 * 1024) {
            resolve(file);
            return;
        }
        var reader = new FileReader();
        reader.onload = function(e) {
            var img = new Image();
            img.onload = function() {
                var w = img.width, h = img.height;
                if (w > maxWidth || h > maxHeight) {
                    var ratio = Math.min(maxWidth / w, maxHeight / h);
                    w = Math.round(w * ratio);
                    h = Math.round(h * ratio);
                }
                var canvas = document.createElement('canvas');
                canvas.width = w;
                canvas.height = h;
                var ctx = canvas.getContext('2d')!;
                ctx.drawImage(img, 0, 0, w, h);
                canvas.toBlob(function(blob) {
                    if (!blob) { resolve(file); return; }
                    var compressed = new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), {
                        type: 'image/jpeg',
                        lastModified: Date.now()
                    });
                    resolve(compressed);
                }, 'image/jpeg', quality);
            };
            img.onerror = function() { resolve(file); };
            img.src = (e.target as FileReader).result as string;
        };
        reader.onerror = function() { resolve(file); };
        reader.readAsDataURL(file);
    });
}

/* ── Label Scan (fotografiere etichetă utilaj) ── */
export function _initLabelScan(btnId: string, fileId: string, statusId: string, previewId: string, fieldPrefix: string): void {
    const btn = document.getElementById(btnId);
    const fileInput = document.getElementById(fileId) as HTMLInputElement | null;
    if (!btn || !fileInput) return;

    btn.addEventListener('click', () => fileInput.click());

    fileInput.addEventListener('change', async () => {
        const files = fileInput.files;
        if (!files || files.length === 0) return;

        const statusEl = document.getElementById(statusId);
        const previewEl = document.getElementById(previewId);

        for (let i = 0; i < files.length; i++) {
            let file = files[i];
            if (!file.type.startsWith('image/')) continue;

            let machineId = '';
            if (fieldPrefix === 'cfg-eq') {
                machineId = (($('#cfg-eq-id') as HTMLInputElement)?.value || '').trim() || 'NEW';
            } else {
                machineId = (($('#edit-eq-id-hidden') as HTMLInputElement)?.value || '').trim() || 'EDIT';
            }

            if (file.size > 4 * 1024 * 1024) {
                (statusEl as HTMLElement).innerHTML = `<span class="scanning">📐 Comprim imaginea (${(file.size/1024/1024).toFixed(1)} MB)...</span>`;
                file = await _compressImage(file, 2048, 2048, 0.85);
            }

            const thumbUrl = URL.createObjectURL(file);
            const img = document.createElement('img');
            img.src = thumbUrl;
            img.title = file.name;
            if (previewEl) previewEl.appendChild(img);

            (statusEl as HTMLElement).innerHTML = `<span class="scanning">📡 Analizez eticheta ${i + 1}/${files.length} cu AI Vision...</span>`;
            (btn as HTMLButtonElement).disabled = true;

            try {
                const formData = new FormData();
                formData.append('photo', file);
                const resp = await fetch(`/api/config/machines/${encodeURIComponent(machineId)}/scan-label`, {
                    method: 'POST',
                    body: formData
                });
                const result = await resp.json();

                if (result.extracted && !result.extracted.error) {
                    const ex = result.extracted;
                    _autoFillFromLabel(fieldPrefix, ex);
                    (statusEl as HTMLElement).innerHTML = `<span class="success">✓ Date extrase: ${ex.manufacturer || '?'} ${ex.model || ''} — S/N: ${ex.serial_number || '?'}</span>`;
                } else {
                    const errMsg = result.extracted?.error || result.error || 'Nu am putut extrage date';
                    (statusEl as HTMLElement).innerHTML = `<span class="error">⚠ ${escHtml(errMsg)}</span>`;
                }
            } catch (e) {
                (statusEl as HTMLElement).innerHTML = `<span class="error">⚠ Eroare: ${escHtml((e as Error).message)}</span>`;
            }
            (btn as HTMLButtonElement).disabled = false;
        }
        fileInput.value = '';
    });
}

export function _autoFillFromLabel(prefix: string, data: any): void {
    const fields = {
        'manufacturer': data.manufacturer,
        'model': data.model,
        'sn': data.serial_number,
        'name': data.name,
        'product': data.description,
    };
    for (const [suffix, value] of Object.entries(fields)) {
        if (!value) continue;
        const el = document.getElementById(`${prefix}-${suffix}`) as HTMLInputElement | null;
        if (el && !el.value.trim()) {
            el.value = value;
            el.style.transition = 'background .5s';
            el.style.background = 'rgba(74,222,128,.15)';
            setTimeout(() => { el.style.background = ''; }, 2000);
        }
    }
}

export function _initPhotoUpload(btnId: string, fileId: string, statusId: string, previewId: string, fieldPrefix: string): void {
    const btn = document.getElementById(btnId);
    const fileInput = document.getElementById(fileId) as HTMLInputElement | null;
    if (!btn || !fileInput) return;

    btn.addEventListener('click', () => fileInput.click());

    fileInput.addEventListener('change', async () => {
        const files = fileInput.files;
        if (!files || files.length === 0) return;

        const statusEl = document.getElementById(statusId);
        const previewEl = document.getElementById(previewId);

        let machineId = '';
        if (fieldPrefix === 'cfg-eq') {
            machineId = (($('#cfg-eq-id') as HTMLInputElement)?.value || '').trim() || 'NEW';
        } else {
            machineId = (($('#edit-eq-id-hidden') as HTMLInputElement)?.value || '').trim() || 'EDIT';
        }

        let uploaded = 0;
        for (let i = 0; i < files.length; i++) {
            let file = files[i];
            if (!file.type.startsWith('image/')) continue;

            if (file.size > 4 * 1024 * 1024) {
                (statusEl as HTMLElement).innerHTML = `<span class="scanning">📐 Comprim poza ${i + 1}/${files.length} (${(file.size/1024/1024).toFixed(1)} MB)...</span>`;
                file = await _compressImage(file, 2048, 2048, 0.85);
            }

            const thumbUrl = URL.createObjectURL(file);
            const img = document.createElement('img');
            img.src = thumbUrl;
            img.title = file.name;
            if (previewEl) previewEl.appendChild(img);

            (statusEl as HTMLElement).innerHTML = `<span class="scanning">⬆ Se încarcă poza ${i + 1}/${files.length}...</span>`;
            try {
                const formData = new FormData();
                formData.append('photo', file);
                formData.append('photo_type', 'general');
                await fetch(`/api/config/machines/${encodeURIComponent(machineId)}/photos`, {
                    method: 'POST',
                    body: formData
                });
                uploaded++;
            } catch (e) { /* ignore */ }
        }
        (statusEl as HTMLElement).innerHTML = `<span class="success">✓ ${uploaded} poze încărcate</span>`;
        fileInput.value = '';

        const galleryElId = fieldPrefix === 'edit-eq' ? 'edit-eq-photos-gallery' : '';
        const gallery = galleryElId ? document.getElementById(galleryElId) : null;
        if (gallery && machineId !== 'NEW') _loadMachinePhotos(machineId, gallery.id);
    });
}

export async function _loadMachinePhotos(machineId: string, galleryId: string): Promise<void> {
    const gallery = document.getElementById(galleryId);
    if (!gallery) return;
    gallery.innerHTML = '';
    try {
        const photos = await api(`/api/config/machines/${encodeURIComponent(machineId)}/photos`);
        if (!photos || photos.length === 0) return;
        photos.forEach((p: any) => {
            const thumb = document.createElement('div');
            thumb.className = 'mp-thumb';
            thumb.innerHTML = `
                <img src="/api/config/machines/photos/${p.id}/file" alt="${escHtml(p.photo_type)}" title="${escHtml(p.description || p.photo_type || '')} — ${p.created_at || ''}">
                <button class="mp-delete" title="Șterge poza" data-photo-id="${p.id}">&times;</button>
            `;
            (thumb.querySelector('.mp-delete') as HTMLElement).addEventListener('click', async (e) => {
                e.stopPropagation();
                if (!await askConfirm('Ștergi această poză?')) return;
                await fetch(`/api/config/machines/photos/${p.id}`, { method: 'DELETE' });
                thumb.remove();
            });
            gallery.appendChild(thumb);
        });
    } catch (e) { /* ignore */ }
}

export function buildSensorConfigRow(cfg: any): HTMLElement {
    const isExtern = !!(cfg.connection_ip || '').trim();
    const isShared = !!(cfg.shared_sensor_id != null && cfg.shared_sensor_id !== '');
    const div = document.createElement('div');
    div.className = 'sensor-config-row' + (isShared ? ' sensor-row-shared' : '');
    if (isShared) div.dataset.sharedSensorId = cfg.shared_sensor_id;
    div.innerHTML = `
        <input class="sc-key" placeholder="key" value="${escHtml(cfg.sensor_key || '')}" title="Cheie senzor (ex. temperature, consumption)">
        <input class="sc-label" placeholder="Label" value="${escHtml(cfg.label || '')}">
        <input class="sc-unit" placeholder="Unit" value="${escHtml(cfg.unit || '')}">
        <input class="sc-num" type="number" step="any" title="Bază" placeholder="Bază" value="${cfg.base_value || 0}">
        <input class="sc-num" type="number" step="any" title="Var%" placeholder="V%" value="${cfg.variance_pct || 4}" style="width:45px">
        <input class="sc-num" type="number" step="any" title="Warn" placeholder="⚠" value="${cfg.warn_threshold || 0}" style="border-color:rgba(255,214,0,.3)">
        <input class="sc-num" type="number" step="any" title="Crit" placeholder="🔴" value="${cfg.crit_threshold || 0}" style="border-color:rgba(255,61,61,.3)">
        <select class="sc-source" title="Sursă: de la utilaj (conexiunea utilajului) sau extern (rețea apă, aer)">
            <option value="" ${!isExtern ? 'selected' : ''}>De la utilaj</option>
            <option value="extern" ${isExtern ? 'selected' : ''}>Extern (apă, aer…)</option>
        </select>
        <span class="sc-conn-wrap" style="display:${isExtern ? 'inline' : 'none'}">
            <input class="sc-conn-ip" placeholder="IP extern" value="${escHtml((cfg.connection_ip || '').trim())}" maxlength="45" style="width:100px">
            <input class="sc-conn-port" type="number" placeholder="Port" value="${escHtml((cfg.connection_port || '').trim())}" min="1" max="65535" style="width:55px">
        </span>
        <button class="sc-del" title="Șterge senzor">×</button>
    `;
    if (isShared) {
        (div.querySelector('.sc-del') as HTMLElement).style.visibility = 'hidden';
        div.querySelectorAll('input, select').forEach((el: Element) => { (el as HTMLInputElement | HTMLSelectElement).disabled = true; });
    } else {
        (div.querySelector('.sc-del') as HTMLElement).addEventListener('click', () => div.remove());
    }
    (div.querySelector('.sc-source') as HTMLSelectElement).addEventListener('change', function (this: HTMLSelectElement) {
        const wrap = div.querySelector('.sc-conn-wrap');
        if (wrap) (wrap as HTMLElement).style.display = this.value === 'extern' ? 'inline' : 'none';
    });
    return div;
}

export function getSensorConfigsFromContainer(containerId: string): any[] {
    const container = document.getElementById(containerId);
    if (!container) return [];
    const rows = container.querySelectorAll('.sensor-config-row');
    return Array.from(rows).map(row => {
        const rEl = row as HTMLElement;
        if (rEl.dataset.sharedSensorId) return null;
        const keyInp = rEl.querySelector('.sc-key') as HTMLInputElement | null;
        const labelInp = rEl.querySelector('.sc-label') as HTMLInputElement | null;
        const unitInp = rEl.querySelector('.sc-unit') as HTMLInputElement | null;
        const numInps = rEl.querySelectorAll('.sc-num');
        const sourceSel = rEl.querySelector('.sc-source') as HTMLSelectElement | null;
        const connIp = rEl.querySelector('.sc-conn-ip') as HTMLInputElement | null;
        const connPort = rEl.querySelector('.sc-conn-port') as HTMLInputElement | null;
        if (!keyInp || !labelInp || numInps.length < 4) return null;
        const sensor_key = keyInp.value.trim();
        const label = labelInp.value.trim();
        if (!sensor_key || !label) return null;
        const out: any = {
            sensor_key,
            label: label,
            unit: (unitInp && unitInp.value) ? unitInp.value.trim() : '',
            base_value: parseFloat((numInps[0] as HTMLInputElement).value) || 0,
            variance_pct: parseFloat((numInps[1] as HTMLInputElement).value) || 4,
            warn_threshold: parseFloat((numInps[2] as HTMLInputElement).value) || 0,
            crit_threshold: parseFloat((numInps[3] as HTMLInputElement).value) || 0,
            enabled: true,
        };
        // Întotdeauna trimitem sursa: Extern = IP/port din câmpuri, De la utilaj = gol (ca să se salveze corect)
        if (sourceSel) {
            if (sourceSel.value === 'extern') {
                out.connection_ip = (connIp && connIp.value) ? String(connIp.value).trim() : '';
                out.connection_port = (connPort && connPort.value) ? String(connPort.value).trim() : '';
            } else {
                out.connection_ip = '';
                out.connection_port = '';
            }
        }
        return out;
    }).filter(Boolean);
}

export function getSensorConfigsFromForm(): any[] {
    return getSensorConfigsFromContainer('edit-eq-sensors');
}

export async function openEditEquipModal(eq: any, fromLineId?: string): Promise<void> {
    await loadMachineTypes();
    populateTypeSelect($('#edit-eq-type'));
    $('#edit-eq-id-hidden').value = eq.id;
    $('#edit-eq-id-hidden').dataset.fromLine = fromLineId || '';
    $('#edit-eq-title').textContent = eq.id;
    const idEditEl = document.getElementById('edit-eq-id') as HTMLInputElement | null;
    if (idEditEl) {
        idEditEl.value = eq.id;
        const isMaster = state.currentUser && state.currentUser.role === 'master';
        idEditEl.readOnly = !isMaster;
        idEditEl.classList.toggle('input-readonly-soft', !isMaster);
        idEditEl.title = isMaster
            ? 'Poți schimba ID-ul; la salvare se actualizează toate referințele din baza de date.'
            : 'Doar utilizatorul master poate schimba ID-ul utilajului.';
    }
    const telHint = document.getElementById('edit-eq-telemetry-id');
    if (telHint) telHint.textContent = eq.id;
    $('#edit-eq-type').value = eq.machine_type || 'auxiliary';
    const nameInput = document.getElementById('edit-eq-name') as HTMLInputElement | null;
    if (nameInput) {
        nameInput.value = eq.name || '';
        nameInput.disabled = false;
        nameInput.readOnly = false;
        nameInput.removeAttribute('disabled');
        nameInput.removeAttribute('readonly');
    } else if ($('#edit-eq-name')) {
        $('#edit-eq-name').value = eq.name || '';
    }
    $('#edit-eq-manufacturer').value = eq.manufacturer || '';
    $('#edit-eq-model').value = eq.model || '';
    $('#edit-eq-sn').value = eq.serial_number || '';
    $('#edit-eq-product').value = eq.product || '';
    $('#edit-eq-notes').value = eq.notes || '';
    const manualPowerEl = document.getElementById('edit-eq-manual-power') as HTMLInputElement | null;
    if (manualPowerEl) manualPowerEl.value = (eq.manual_power_kw != null && eq.manual_power_kw > 0) ? String(eq.manual_power_kw) : '';
    const hmiUrlEl = document.getElementById('edit-eq-hmi-url') as HTMLInputElement | null;
    if (hmiUrlEl) hmiUrlEl.value = (eq.hmi_url || '').trim();
    const editConnType = document.getElementById('edit-eq-connection-type') as HTMLSelectElement | null;
    const editConnIp = document.getElementById('edit-eq-connection-ip') as HTMLInputElement | null;
    const editConnPort = document.getElementById('edit-eq-connection-port') as HTMLInputElement | null;
    if (editConnType) editConnType.value = (eq.connection_type || '').trim() || '';
    if (editConnIp) editConnIp.value = (eq.connection_ip || '').trim() || '';
    if (editConnPort) editConnPort.value = (eq.connection_port != null && eq.connection_port !== '') ? String(eq.connection_port) : '';

    await buildLinesCheckboxes('edit-eq-lines-checkboxes', eq.line || '');

    const linkedWrap = document.getElementById('edit-eq-linked-wrap') as HTMLElement;
    const cavitiesWrap = document.getElementById('edit-eq-cavities-wrap');
    const linkedSel = document.getElementById('edit-eq-linked-machine') as HTMLSelectElement;
    if (eq.machine_type === 'mold') {
        linkedWrap.style.display = '';
        if (cavitiesWrap) cavitiesWrap.style.display = '';
        const cavInput = document.getElementById('edit-eq-cavities') as HTMLInputElement | null;
        if (cavInput) cavInput.value = (eq.cavities != null && eq.cavities !== '') ? String(eq.cavities) : '';
        const allMach = await api('/api/machines/all');
        linkedSel.innerHTML = '<option value="">— neselectat —</option>';
        allMach.filter((m: any) => m.machine_type === 'injection' || m.machine_type === 'compression')
            .forEach((m: any) => {
                const opt = document.createElement('option');
                opt.value = m.id;
                opt.textContent = `${m.id} — ${m.name}`;
                linkedSel.appendChild(opt);
            });
        linkedSel.value = eq.linked_machine_id || '';
    } else {
        linkedWrap.style.display = 'none';
        if (cavitiesWrap) cavitiesWrap.style.display = 'none';
    }

    const sensorsEl = $('#edit-eq-sensors');
    sensorsEl.innerHTML = '<div style="color:var(--text-muted);font-size:.65rem;margin-bottom:.3rem">Key · Label · Unit · Bază · Var% · ⚠Warn · 🔴Crit · Sursă (De la utilaj / Extern) · IP/Port</div>';
    const sensorCfgs = await api(`/api/config/sensors/${encodeURIComponent(eq.id)}`);
    // Pentru consum: dacă mașina are contor configurat dar senzorul nu are IP, precompletăm ca „Extern" cu IP contor
    const meterIp = (eq.meter_connection_ip || '').trim();
    const meterPort = (eq.meter_connection_port || '').trim() || '502';
    sensorCfgs.forEach((cfg: any) => {
        if ((cfg.sensor_key === 'consum' || cfg.sensor_key === 'consumption') && !(cfg.connection_ip || '').trim() && meterIp) {
            cfg.connection_ip = meterIp;
            cfg.connection_port = meterPort;
        }
    });
    sensorCfgs.forEach((cfg: any) => sensorsEl.appendChild(buildSensorConfigRow(cfg)));

    const delBtn = document.getElementById('btn-delete-equip');
    if (delBtn) {
        (delBtn as HTMLElement).style.display = (state.currentUser.role === 'master') ? '' : 'none';
    }

    const scanStatusEdit = document.getElementById('scan-label-status-edit');
    const scanPreviewEdit = document.getElementById('scan-label-preview-edit');
    if (scanStatusEdit) scanStatusEdit.innerHTML = '';
    if (scanPreviewEdit) scanPreviewEdit.innerHTML = '';
    _loadMachinePhotos(eq.id, 'edit-eq-photos-gallery');

    await _loadLifecycleIntoEditModal(eq.id);

    $('#modal-edit-equip').classList.remove('hidden');
}

async function _loadLifecycleIntoEditModal(machineId: string): Promise<void> {
    const cdEl = document.getElementById('edit-eq-commissioning-date') as HTMLInputElement | null;
    const mdEl = document.getElementById('edit-eq-manufacture-date') as HTMLInputElement | null;
    const noteEl = document.getElementById('edit-eq-commissioning-notes') as HTMLTextAreaElement | null;
    const badgeEl = document.getElementById('edit-eq-lifecycle-badge') as HTMLElement | null;
    const battWrap = document.getElementById('edit-eq-battery-task-wrap') as HTMLElement | null;
    const battStatus = document.getElementById('edit-eq-battery-task-status') as HTMLElement | null;
    if (cdEl) cdEl.value = '';
    if (mdEl) mdEl.value = '';
    if (noteEl) noteEl.value = '';
    if (badgeEl) { badgeEl.style.display = 'none'; badgeEl.innerHTML = ''; }
    if (battWrap) battWrap.style.display = 'none';
    if (battStatus) battStatus.textContent = '';
    try {
        const info = await api(`/api/config/machines/${encodeURIComponent(machineId)}/lifecycle`) as LifecycleInfo;
        if (cdEl) cdEl.value = info.commissioning_date || '';
        if (mdEl) mdEl.value = info.manufacture_date || '';
        if (noteEl) noteEl.value = info.commissioning_notes || '';
        if (badgeEl) {
            const b = buildLifecycleBadge(info);
            badgeEl.innerHTML = b.html;
            badgeEl.style.display = '';
        }
        if (battWrap && info.battery_eligible) {
            battWrap.style.display = '';
            battWrap.dataset.machineId = machineId;
            if ((info.age_years ?? 0) >= 4 && battStatus) {
                battStatus.textContent = `(recomandat — utilaj are ${info.age_years!.toFixed(1)} ani)`;
            }
        }
    } catch (e) {
        if (badgeEl) badgeEl.style.display = 'none';
    }
}

$('#edit-eq-type').addEventListener('change', () => {
    const linkedWrap = document.getElementById('edit-eq-linked-wrap') as HTMLElement;
    const cavitiesWrap = document.getElementById('edit-eq-cavities-wrap');
    const isMold = $('#edit-eq-type').value === 'mold';
    linkedWrap.style.display = isMold ? '' : 'none';
    if (cavitiesWrap) cavitiesWrap.style.display = isMold ? '' : 'none';
});

$('#btn-add-sensor').addEventListener('click', () => {
    $('#edit-eq-sensors').appendChild(buildSensorConfigRow({
        sensor_key: '', label: '', unit: '°C', base_value: 25, variance_pct: 5, warn_threshold: 50, crit_threshold: 70
    }));
});

const _btnGenBattery = document.getElementById('btn-eq-generate-battery-task');
if (_btnGenBattery) {
    _btnGenBattery.addEventListener('click', async () => {
        const wrap = document.getElementById('edit-eq-battery-task-wrap') as HTMLElement | null;
        const mid = (wrap && wrap.dataset.machineId) || ($('#edit-eq-id-hidden') as HTMLInputElement).value;
        if (!mid) { toast('ID utilaj lipsă', 'error'); return; }
        if (!await askConfirm(`Generezi un task preventiv „Schimbare baterii sistem comandă" la 5 ani pentru ${mid}?`, `Va apărea în lista de intervale de mentenanță. Idempotent — nu duplică dacă există deja.`)) return;
        try {
            const r = await fetch(`/api/config/machines/${encodeURIComponent(mid)}/battery-task`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: '{}'
            });
            const d = await r.json().catch(() => ({}));
            if (!r.ok) { toast(d.error || 'Eroare la generare task', 'error'); return; }
            if (d.status === 'exists') {
                toast('Task baterii există deja pentru această mașină.');
            } else {
                toast(`Task baterii generat (interval #${d.interval_id}). Verifică lista de intervale.`);
            }
        } catch (e) {
            toast('Eroare: ' + (e as Error).message, 'error');
        }
    });
}

$('#btn-update-equip').addEventListener('click', async () => {
    const origId = ($('#edit-eq-id-hidden').value || '').trim();
    const idField = document.getElementById('edit-eq-id') as HTMLInputElement | null;
    let mid = (idField && idField.value ? idField.value : origId).trim();
    if (!origId) return;
    if (!mid) {
        toast('ID utilaj obligatoriu', 'error');
        return;
    }
    if (mid !== origId) {
        if (!state.currentUser || state.currentUser.role !== 'master') {
            toast('Doar master poate schimba ID-ul utilajului.', 'error');
            return;
        }
        try {
            const rr = await fetch(`/api/config/machines/${encodeURIComponent(origId)}/rename`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ new_id: mid }),
            });
            const rd = await rr.json().catch(() => ({}));
            if (!rr.ok) {
                toast(rd.error || 'Redenumire ID eșuată', 'error');
                return;
            }
            $('#edit-eq-id-hidden').value = mid;
            $('#edit-eq-title').textContent = mid;
            const th = document.getElementById('edit-eq-telemetry-id');
            if (th) th.textContent = mid;
        } catch (e) {
            toast('Eroare redenumire: ' + (e as Error).message, 'error');
            return;
        }
    }

    const lineVal = getCheckedLines('edit-eq-lines-checkboxes');
    const payload: any = {
        machine_type: $('#edit-eq-type').value,
        name: $('#edit-eq-name').value.trim(),
        manufacturer: $('#edit-eq-manufacturer').value.trim(),
        model: $('#edit-eq-model').value.trim(),
        serial_number: $('#edit-eq-sn').value.trim(),
        product: $('#edit-eq-product').value.trim(),
        line: lineVal,
        notes: $('#edit-eq-notes').value.trim(),
    };
    const mpEl = document.getElementById('edit-eq-manual-power');
    if (mpEl) {
        const mpVal = parseFloat((mpEl as HTMLInputElement).value);
        payload.manual_power_kw = (!isNaN(mpVal) && mpVal > 0) ? mpVal : null;
    }
    const hmiUrlEditEl = document.getElementById('edit-eq-hmi-url');
    if (hmiUrlEditEl) payload.hmi_url = (hmiUrlEditEl as HTMLInputElement).value.trim() || null;
    const editCt = document.getElementById('edit-eq-connection-type');
    const editCi = document.getElementById('edit-eq-connection-ip');
    const editCp = document.getElementById('edit-eq-connection-port');
    if (editCt) payload.connection_type = ((editCt as HTMLSelectElement).value || '').trim() || '';
    if (editCi) payload.connection_ip = ((editCi as HTMLInputElement).value || '').trim() || '';
    if (editCp && (editCp as HTMLInputElement).value !== '') payload.connection_port = (editCp as HTMLInputElement).value; else payload.connection_port = '';
    if ($('#edit-eq-type').value === 'mold') {
        payload.linked_machine_id = (document.getElementById('edit-eq-linked-machine') as HTMLSelectElement).value;
        const cav = document.getElementById('edit-eq-cavities');
        payload.cavities = (cav && (cav as HTMLInputElement).value && parseInt((cav as HTMLInputElement).value, 10)) || null;
    }

    const cdEditEl = document.getElementById('edit-eq-commissioning-date') as HTMLInputElement | null;
    const mdEditEl = document.getElementById('edit-eq-manufacture-date') as HTMLInputElement | null;
    const cnoteEditEl = document.getElementById('edit-eq-commissioning-notes') as HTMLTextAreaElement | null;
    if (cdEditEl) payload.commissioning_date = (cdEditEl.value || '').trim() || null;
    if (mdEditEl) payload.manufacture_date = (mdEditEl.value || '').trim() || null;
    if (cnoteEditEl) payload.commissioning_notes = (cnoteEditEl.value || '').trim();

    try {
        const res = await fetch(`/api/config/machines/${encodeURIComponent(mid)}`, { method: 'PUT', headers: {'Content-Type':'application/json'}, body: JSON.stringify(payload) });
        const data = await res.json();
        if (!res.ok) { toast(data.error || 'Eroare'); return; }

        const sensors = getSensorConfigsFromForm();
        await fetch(`/api/config/sensors/${encodeURIComponent(mid)}`, { method: 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify({ sensors }) });

        toast(`${mid} actualizat!`);
        $('#modal-edit-equip').classList.add('hidden');
        loadOverview();
    } catch (e) { toast('Eroare: ' + (e as Error).message); }
});

$('#btn-delete-equip').addEventListener('click', async () => {
    const mid = $('#edit-eq-id-hidden').value;
    if (!mid) return;
    if (state.currentUser.role !== 'master') {
        toast('Doar nivel master poate șterge utilaje.', 'error');
        return;
    }

    const fromLine = $('#edit-eq-id-hidden').dataset.fromLine || '';
    const linesCheckboxes = document.querySelectorAll('#edit-eq-lines-checkboxes input[type="checkbox"]:checked');
    const currentLines = Array.from(linesCheckboxes).map(cb => (cb as HTMLInputElement).value);
    const isMultiLine = currentLines.length > 1 && fromLine;

    if (isMultiLine) {
        const choice = await askChoice(`⚠️ ${mid} e pe ${currentLines.length} linii`,
            `Utilajul este alocat la: ${currentLines.join(', ')}.`, [
            { key: '1', label: `Elimină DOAR de la linia ${fromLine} (rămâne la celelalte)` },
            { key: '2', label: 'Șterge utilajul COMPLET (toate liniile + toate datele)', danger: true },
        ]);

        if (choice === '1') {
            const newLines = currentLines.filter(l => l !== fromLine).join(',');
            try {
                const res = await fetch(`/api/config/machines/${encodeURIComponent(mid)}`, {
                    method: 'PUT',
                    headers: {'Content-Type':'application/json'},
                    body: JSON.stringify({ line: newLines })
                });
                if (!res.ok) { const d = await res.json(); toast(d.error || 'Eroare', 'error'); return; }
                toast(`${mid} eliminat de la linia ${fromLine}. Rămâne la: ${newLines}`);
                $('#modal-edit-equip').classList.add('hidden');
                loadOverview();
            } catch (e) { toast('Eroare: ' + (e as Error).message, 'error'); }
            return;
        } else if (choice !== '2') {
            return;
        }
    }

    if (!await askConfirm(`⚠️ ATENȚIE: Sigur ștergi COMPLET utilajul ${mid}?`, `Se va elimina de la TOATE liniile și toate datele asociate.\nAcțiunea NU poate fi anulată!`)) return;

    try {
        const res = await fetch(`/api/config/machines/${encodeURIComponent(mid)}`, { method: 'DELETE' });
        const data = await res.json();
        if (!res.ok) { toast(data.error || 'Eroare'); return; }
        toast(`${mid} șters complet!`);
        $('#modal-edit-equip').classList.add('hidden');
        loadOverview();
    } catch (e) { toast('Eroare: ' + (e as Error).message); }
});
